import { useEffect, useState, type JSX, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { Header } from "@/shared/layout/Header";
import { Sidebar } from "@/shared/layout/Sidebar";

export interface AppLayoutProps {
  appName: string;
  children: ReactNode;
}

export function AppLayout({ appName, children }: AppLayoutProps): JSX.Element {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isSidebarOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!isSidebarOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSidebarOpen]);

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-gray-900">
      <Sidebar
        appName={appName}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex min-h-screen flex-col lg:pl-72">
        <Header appName={appName} onOpenMenu={() => setIsSidebarOpen(true)} />

        <main className="min-w-0 flex-1 bg-[#F9FAFB]">
          <div className="mx-auto flex w-full max-w-7xl flex-col p-6 lg:p-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
